/**
 * Unified Report Exporter Utility
 *
 * Provides simplified CSV export functionality for the UnifiedReportsTable component.
 * This is a lightweight alternative to the more complex nodeReportExporter.ts which
 * supports both CSV and Excel formats with statistics.
 *
 * Usage:
 * ```typescript
 * import { exportToCSV } from '@/utils/export/unifiedReportExporter';
 *
 * const handleExport = () => {
 *   exportToCSV(reports, ['reportId', 'date', 'source', 'content']);
 * };
 * ```
 */

import {UnifiedReportRow} from '../../@types/integrated-risk-picture';
import {downloadCsvFile} from './csv';

/**
 * Column identifiers that can be exported
 */
export type ExportableColumn =
    | 'reportId'
    | 'date'
    | 'source'
    | 'content'
    | 'justification'
    | 'confidence'
    | 'mappingType';

/**
 * Column definitions for CSV export
 */
interface ColumnDefinition {
    key: ExportableColumn;
    label: string;
    getValue: (row: UnifiedReportRow) => string | number;
}

/**
 * Default column definitions
 */
const COLUMN_DEFINITIONS: Record<ExportableColumn, ColumnDefinition> = {
    reportId: {
        key: 'reportId',
        label: 'Report ID',
        getValue: (row): string => row.uniqueId
    },
    date: {
        key: 'date',
        label: 'Date',
        getValue: (row): string => {
            if (!row.dateTime) return 'N/A';
            try {
                const date = row.dateTime instanceof Date
                    ? row.dateTime
                    : new Date(row.dateTime);
                return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                });
            } catch {
                return 'Invalid Date';
            }
        }
    },
    source: {
        key: 'source',
        label: 'Source',
        getValue: (row): string => row.sourceName || 'Unknown'
    },
    content: {
        key: 'content',
        label: 'Content',
        getValue: (row): string => {
            // Clean up content for CSV: replace newlines with spaces
            return (row.content || 'No content').replace(/[\n\r]+/g, ' ').trim();
        }
    },
    justification: {
        key: 'justification',
        label: 'AI Justification',
        getValue: (row): string => {
            return (row.justification || 'N/A').replace(/[\n\r]+/g, ' ').trim();
        }
    },
    confidence: {
        key: 'confidence',
        label: 'Confidence Score',
        getValue: (row): string => {
            if (row.confidence === undefined) return 'N/A';
            return `${(row.confidence * 100).toFixed(1)}%`;
        }
    },
    mappingType: {
        key: 'mappingType',
        label: 'Mapping Type',
        getValue: (row): string => {
            if (row.isManual === undefined) return 'N/A';
            return row.isManual ? 'Manual' : 'AI';
        }
    }
};

/**
 * Escape CSV values that contain special characters
 *
 * @param value - The value to escape
 * @returns Properly escaped CSV value
 */
export function escapeCSVValue(value: string | number): string {
    const stringValue = String(value);

    // Always wrap in quotes if contains special characters
    if (stringValue.includes(',') ||
        stringValue.includes('"') ||
        stringValue.includes('\n') ||
        stringValue.includes('\r')) {
        // Escape internal quotes by doubling them
        const escaped = stringValue.replace(/"/g, '""');
        return `"${escaped}"`;
    }

    return stringValue;
}

/**
 * Format timestamp for filename generation
 *
 * @returns Timestamp string in YYYY-MM-DD_HH-mm-ss format
 */
export function formatTimestamp(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
}

/**
 * Generate CSV content from unified report rows
 *
 * @param reports - Array of unified report rows
 * @param columns - Array of column keys to include in export
 * @returns CSV string with headers and data
 */
export function generateCSV(
    reports: UnifiedReportRow[],
    columns: ExportableColumn[]
): string {
    // Get column definitions for selected columns
    const selectedColumns = columns.map(key => COLUMN_DEFINITIONS[key]);

    // Generate header row
    const headerRow = selectedColumns
        .map(col => escapeCSVValue(col.label))
        .join(',');

    // Generate data rows
    const dataRows = reports.map(report => {
        return selectedColumns
            .map(col => escapeCSVValue(col.getValue(report)))
            .join(',');
    });

    // Combine header and data
    return [headerRow, ...dataRows].join('\n');
}

/**
 * Export unified report rows to CSV file
 *
 * @param reports - Array of unified report rows to export
 * @param columns - Array of column keys to include in export
 * @param filename - Optional custom filename (without extension)
 */
export function exportToCSV(
    reports: UnifiedReportRow[],
    columns: ExportableColumn[],
    filename?: string
): void {
    // Generate filename
    const timestamp = formatTimestamp();
    const csvFilename = filename
        ? `${filename}_${timestamp}.csv`
        : `irp-reports_${timestamp}.csv`;

    // Generate CSV content
    const csvContent = generateCSV(reports, columns);

    downloadCsvFile(csvFilename, csvContent);
}
